import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RecruitCandidates, canInvite } from "@/components/admin/RecruitCandidates";

const Area = ({ label, hint, testid, rows = 4, ...p }) => (
  <label className="block mt-4">
    <span className="text-sm font-medium text-[#1E3F33]">{label}</span>
    {hint && <span className="block text-[11px] text-[#8A9A86]">{hint}</span>}
    <textarea rows={rows} className="input-soft mt-1 text-sm" data-testid={testid} {...p} />
  </label>
);

const summary = (r) => {
  const parts = [`${r.collectors.sent} collector${r.collectors.sent === 1 ? "" : "s"}`, `${r.outside.sent} outside contact${r.outside.sent === 1 ? "" : "s"}`];
  const skipped = r.collectors.skipped_recent + r.outside.skipped_recent;
  const extra = [skipped && `${skipped} invited in the last 14 days`, r.outside.already_registered && `${r.outside.already_registered} already registered`,
    r.outside.unsubscribed && `${r.outside.unsubscribed} unsubscribed`, r.outside.phones_skipped && `${r.outside.phones_skipped} phone numbers skipped (email only)`,
    r.collectors.no_channel && `${r.collectors.no_channel} with no opted-in contact`, r.outside.invalid?.length && `${r.outside.invalid.length} unrecognized`,
    (r.collectors.failed + r.outside.failed) && `${r.collectors.failed + r.outside.failed} failed`].filter(Boolean);
  return `Invites sent to ${parts.join(" and ")}${extra.length ? ` · Skipped: ${extra.join(", ")}` : ""}`;
};

export const RecruitDialog = ({ target, onClose, onSent }) => {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [msg, setMsg] = useState({ collector_message: "", outside_subject: "", outside_message: "" });
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!target) return;
    setData(null); setEmails("");
    api.get("/admin/recruit/candidates", { params: { zip: target.zip, service: target.code } })
      .then(({ data }) => { setData(data); setMsg(data.defaults); setSelected(data.collectors.filter(canInvite).map((c) => c.id)); })
      .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); onClose(); });
  }, [target, onClose]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const send = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/recruit/send", { zip: target.zip, service: target.code, collector_ids: selected, outside_emails: emails, ...msg });
      toast.success(summary(r));
      onSent();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="recruit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[#1E3F33] flex items-center gap-2"><Megaphone className="w-5 h-5 text-[#CC5A3A]" /> Recruit near {target?.zip}</DialogTitle>
          <DialogDescription>Invite collectors to cover <span className="font-medium text-[#1E3F33]">{target?.label}</span>. {data && <span data-testid="recruit-dialog-stats">Invited {data.stats.invited} · {data.stats.converted} signed up/added service.</span>}</DialogDescription>
        </DialogHeader>
        {!data ? <div className="text-sm text-[#5C6B64] py-6">Loading…</div> : (
          <div>
            <div className="text-sm font-medium text-[#1E3F33] mb-2">Registered collectors nearby ({data.collectors.length})</div>
            <RecruitCandidates collectors={data.collectors} selected={selected} onToggle={toggle} />
            <Area label="Message to collectors" hint="Email + SMS (only to collectors who agreed to alerts). Placeholders: {name} {zip} {service} {action} {link}" testid="recruit-collector-message" rows={3} value={msg.collector_message} onChange={(e) => setMsg((m) => ({ ...m, collector_message: e.target.value }))} />
            <Area label="Outside contacts (emails)" hint="Paste emails separated by commas, spaces or new lines. Email only, with an unsubscribe link. Phone numbers are skipped." testid="recruit-outside-emails" rows={2} value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="name@clinic.com, other@lab.com" />
            {emails.trim() && (
              <>
                <label className="block mt-4"><span className="text-sm font-medium text-[#1E3F33]">Email subject</span>
                  <input className="input-soft mt-1 text-sm" data-testid="recruit-outside-subject" value={msg.outside_subject} onChange={(e) => setMsg((m) => ({ ...m, outside_subject: e.target.value }))} /></label>
                <Area label="Message to outside contacts" hint="{link} becomes a collector sign-up link." testid="recruit-outside-message" rows={6} value={msg.outside_message} onChange={(e) => setMsg((m) => ({ ...m, outside_message: e.target.value }))} />
              </>
            )}
            <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
              <span className="text-xs text-[#8A9A86]">The same person isn't re-invited for this ZIP + service within {data.cooldown_days} days.</span>
              <button data-testid="recruit-send-btn" onClick={send} disabled={busy || (!selected.length && !emails.trim())} className="btn-primary inline-flex items-center gap-2 text-sm py-2 px-4 disabled:opacity-50">
                <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send invites"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
